<template>
  <div class="row full-width full-height">
    <div class="column col-md-3 col-sm-12 items-center re-gutter-y q-pr-md">
      <re-multi-choice
        v-model="talkjs.showGeneralInbox"
        size="lg"
        wide-options
        class="full-width"
        :options="[
          { label: $t('monolith.inbox.my_inbox'), value: false },
          { label: $t('monolith.inbox.general_inbox'), value: true },
        ]"
      />
      <re-multi-choice
        v-model="filters"
        class="inbox-filters"
        :label="$t('monolith.inbox.filters')"
        size="md"
        is-separated
        is-half-width
        multiple
        :options="filterOptions"
        @update:model-value="updateFilters"
      />

      <re-select
        v-model="userFilter"
        class="full-width"
        :label="$t('monolith.inbox.user')"
        use-input
        hide-selected
        fill-input
        :options="userOptions"
        @filter="filterFn"
        @update:model-value="changeUserFilter"
      />
    </div>

    <div
      v-show="!talkjs.showGeneralInbox"
      :id="talkjs.INBOX_ID"
      class="col-sm-12 col-md-9 row talkjs-container"
    />
    <div
      v-show="talkjs.showGeneralInbox"
      :id="talkjs.SUPPORT_INBOX_ID"
      class="col-sm-12 col-md-9 row talkjs-container"
    />
  </div>
  <re-dialog
    v-model="talkjs.showTags"
    has-cancel
    :title="$t('monolith.inbox.add_tags')"
    persistent
    @confirm="persistTags"
  >
    <re-multi-choice
      v-model="talkjs.tags"
      class="inbox-filters"
      size="md"
      is-separated
      is-half-width
      multiple
      :options="tagsOptions"
      @update:model-value="updateTags"
    />
  </re-dialog>

  <re-dialog
    v-model="talkjs.showAddUser"
    has-cancel
    :title="$t('monolith.inbox.add_user')"
    persistent
    @confirm="addUser"
  >
    <q-select
      v-model="user"
      use-input
      hide-selected
      fill-input
      dense
      bg-color="light"
      :options="addUserOptions"
      @filter="addFilterFn"
    />
  </re-dialog>

  <re-dialog
    v-model="talkjs.showRemoveUser"
    has-cancel
    :title="$t('monolith.inbox.remove_user')"
    persistent
    @confirm="removeUser"
  >
    <q-select
      v-model="user"
      use-input
      hide-selected
      fill-input
      dense
      bg-color="light"
      :options="removeUserOptions"
      @filter="removeFilterFn"
    />
  </re-dialog>

  <re-dialog
    v-model="showConversationsFromUser"
    has-close-button
    wide-dialog
    :title="`${$t('monolith.inbox.conversations')}${$t('punctuation.:')} ${usersList.find((item) => item.value === userFilter)?.label}`"
    persistent
    class="conversations-dialog col-12"
    @close="closeConversationsFromUser"
  >
    <conversations-overview :conversations="conversationsFromUser" />
  </re-dialog>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useConfig } from '@/composables/useConfig'
import useUserList from '@/composables/useUserList'
import { talkjsKey } from '@/keys'

import conversationsOverview from './conversationsOverview.vue'

const { t } = useI18n()
const config = useConfig()

const talkjs = inject(talkjsKey)

const showConversationsFromUser = ref(false)
const userFilter = ref()
const user = ref()
const filters = ref([])
const userOptions = ref([])
const addUserOptions = ref([])
const removeUserOptions = ref([])
const conversationsFromUser = ref([])
const participants = ref([])

// we need to create here because ids used on talkjs need to be present on the DOM
onMounted(async () => {
  await talkjs.createInbox()
})

const allUsers = useUserList()
const valuerUsers = useUserList({ roles: 'valuer' })
const supportUsers = useUserList({ roles: 'support' })

watch([() => talkjs.participants], ([snapshot]) => {
  if (!snapshot) {
    return
  }

  participants.value = snapshot
    .filter((item) => item.user.id !== talkjs.SUPPORT_USER.id)
    .map((item) => ({
      label: item.user.name,
      value: item.user.id,
      ...item.user,
    }))
})

watch(
  () => talkjs.showGeneralInbox,
  (newVal) => {
    filters.value = newVal ? [] : ['is_support_involved']
  },
  { immediate: true }
)

const usersList = computed(() => {
  return allUsers.data.map((user) => ({ label: `${user.displayName} (${user.email})`, value: user.id }))
})

const addUsersList = computed(() => {
  const uniqueUsers = supportUsers.data.concat(valuerUsers.data).reduce((accumulator, currentValue) => {
    if (!accumulator.find((item) => item.id === currentValue.id)) {
      accumulator.push(currentValue)
    }
    return accumulator
  }, [])

  return uniqueUsers
    .map((user) => ({ label: `${user.displayName} (${user.email})`, value: user.id }))
    .filter(
      (user) => !participants.value.find((item) => item.value === `${config.ENVIRONMENT_TYPE}:${user.value}`)
    )
})

const tagsOptions = computed(() => {
  return talkjs.TAGS.map((tag) => ({
    label: t(`monolith.inbox.${tag}`),
    value: tag,
  }))
})

const filterOptions = computed(() => {
  return [
    ...tagsOptions.value,
    { label: t('monolith.inbox.archived'), value: 'archived' },
    { label: t('monolith.inbox.support_involved'), value: 'is_support_involved' },
  ]
})

async function changeUserFilter(value) {
  conversationsFromUser.value = await talkjs.getConversations(value)
  showConversationsFromUser.value = true
}

function closeConversationsFromUser() {
  userFilter.value = null
  showConversationsFromUser.value = false
  conversationsFromUser.value = []
}

function updateFilters(value) {
  filters.value = value
  talkjs.setFilters(filters.value)
}

function updateTags(value) {
  talkjs.updateTags(value)
}

function persistTags() {
  talkjs.persistTags()
}

function addUser() {
  talkjs.addUserToConversation(allUsers.data.find((_user) => _user.id === user.value.value))
  user.value = null
}

function removeUser() {
  talkjs.removeUserFromConversation(user.value.value)
  user.value = null
}

function filterFn(val, update) {
  update(() => {
    if (!val || val === '') {
      userOptions.value = usersList.value

      return
    }

    const needle = val.toLowerCase()
    userOptions.value = usersList.value.filter((item) => item.label.toLowerCase().indexOf(needle) > -1)
  })
}

function addFilterFn(val, update) {
  update(() => {
    if (!val || val === '') {
      addUserOptions.value = addUsersList.value

      return
    }

    const needle = val.toLowerCase()
    addUserOptions.value = addUsersList.value.filter((item) => item.label.toLowerCase().indexOf(needle) > -1)
  })
}

function removeFilterFn(val, update) {
  update(() => {
    if (!val || val === '') {
      removeUserOptions.value = participants.value

      return
    }

    const needle = val.toLowerCase()
    removeUserOptions.value = participants.value.filter(
      (item) => item.label.toLowerCase().indexOf(needle) > -1
    )
  })
}
</script>

<style lang="scss" scoped>
.conversations-dialog :deep(.wide-dialog) {
  width: 100% !important;
}

.inbox-filters {
  .re-separated :deep(.row) {
    width: auto;
    white-space: nowrap;
  }
}
</style>
